import AcceptedIcon from "@mui/icons-material/Check";
import RejectedIcon from "@mui/icons-material/Close";
import DraftIcon from "@mui/icons-material/EditOutlined";
import OfferedIcon from "@mui/icons-material/Schedule";
import { Box, SvgIcon, Theme } from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { OfferStatus } from "../../../shared/CaseModel";

/** The statuses of an offer, in the order an offer passes through them. */
export const OFFER_STATUSES: readonly OfferStatus[] = [
  OfferStatus.Draft,
  OfferStatus.Offered,
  OfferStatus.Rejected,
  OfferStatus.Accepted,
];

/** An accepted or a rejected offer is decided: nothing of it changes any more. */
export const isDecided = (status: OfferStatus): boolean =>
  status === OfferStatus.Rejected || status === OfferStatus.Accepted;

/** The key of the name of a status under `case.analytics.settlement.offerDialog.status`. */
export const statusKey = (
  status: OfferStatus,
): "draft" | "offered" | "rejected" | "accepted" => {
  switch (status) {
    case OfferStatus.Draft:
      return "draft";
    case OfferStatus.Offered:
      return "offered";
    case OfferStatus.Rejected:
      return "rejected";
    case OfferStatus.Accepted:
      return "accepted";
  }
};

/** How strongly the head of a card and a status are washed with the colour of the status. */
const WASH_ALPHA = 0.06;
const PILL_ALPHA = 0.12;

/** How far the grey of a draft is lightened from the colour of secondary text. */
const DRAFT_LIGHTEN = 0.45;

export type OfferLook = {
  /**
   * The colour of the status: the edge of the offer's card, and its number,
   * line and arrow in the timeline and on the chart.
   */
  color: string;
  /** The colour of text and icons that name the status. */
  ink: string;
  /** The colour of the number on a mark in the colour of the status. */
  markInk: string;
  /** The wash behind the head of the card. */
  wash: string;
  /** The wash behind the name of the status. */
  pill: string;
  Icon: typeof SvgIcon;
};

/**
 * How an offer of a status is drawn: a draft is grey, an offer that has been
 * made takes the primary colour, a rejected one is red and an accepted one
 * green.
 */
export function offerLook(theme: Theme, status: OfferStatus): OfferLook {
  const { positive, negative } = theme.palette.data;
  switch (status) {
    case OfferStatus.Draft: {
      const grey = lighten(theme.palette.text.secondary, DRAFT_LIGHTEN);
      return {
        color: grey,
        ink: theme.palette.text.secondary,
        // white does not stand out from the grey
        markInk: theme.palette.text.primary,
        wash: alpha(theme.palette.text.secondary, WASH_ALPHA),
        pill: alpha(theme.palette.text.secondary, PILL_ALPHA),
        Icon: DraftIcon,
      };
    }
    case OfferStatus.Offered:
      return {
        color: theme.palette.primary.main,
        ink: theme.palette.primary.main,
        markInk: theme.palette.primary.contrastText,
        wash: alpha(theme.palette.primary.main, WASH_ALPHA),
        pill: alpha(theme.palette.primary.main, PILL_ALPHA),
        Icon: OfferedIcon,
      };
    case OfferStatus.Rejected:
      return {
        color: negative.main,
        ink: negative.dark,
        markInk: negative.contrastText,
        wash: alpha(negative.main, WASH_ALPHA),
        pill: alpha(negative.main, PILL_ALPHA),
        Icon: RejectedIcon,
      };
    case OfferStatus.Accepted:
      return {
        color: positive.main,
        ink: positive.dark,
        markInk: positive.contrastText,
        wash: alpha(positive.main, WASH_ALPHA),
        pill: alpha(positive.main, PILL_ALPHA),
        Icon: AcceptedIcon,
      };
  }
}

/** The status of an offer as a word on a wash of its colour, with its icon. */
export const OfferStatusBadge = ({ status }: { status: OfferStatus }) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.settlement.offerDialog.status",
  });
  const { ink, pill, Icon } = offerLook(theme, status);

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        pl: 0.875,
        pr: 1.25,
        py: 0.375,
        borderRadius: 999,
        bgcolor: pill,
        color: ink,
        typography: "caption",
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <Icon sx={{ fontSize: 15 }} />
      {t(statusKey(status))}
    </Box>
  );
};
